import { FC, useState } from 'react';
import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';

import { Drawer } from 'components/drawer';

import { packageAcquirerEntitySearchAtom } from './atoms';
import { PackageEntityTable } from './package-entity-table';
import type { AcquiringEntity, EntityRow } from './types';
import styles from './entity-picker-panel.module.scss';

/** Props for {@link EntityPickerPanel}. */
export interface EntityPickerPanelProps {
  /** Drives Drawer visibility + inner body mount/unmount (resets pending state). */
  open: boolean;
  /** Currently-saved acquiring-entity. Seeds the inner pending selection. */
  selectedEntity: AcquiringEntity | null;
  /** Called with the projected ref when the user clicks Save. */
  onSelect: (entity: AcquiringEntity) => void;
  /** Called on Cancel, overlay-click, or ESC. */
  onClose: () => void;
}

/** Props for the inner body. */
interface EntityPickerPanelBodyProps {
  selectedEntity: AcquiringEntity | null;
  onSelect: (entity: AcquiringEntity) => void;
  onClose: () => void;
}

/**
 * Project a search-table `EntityRow` to the form-shape `AcquiringEntity`.
 * The form value type stops at the picker-grid display fields — anything
 * else on the row is dropped at the boundary.
 *
 * @param {EntityRow} row - Full search-row from the entity table
 * @return {AcquiringEntity} Form-shape acquirer
 */
const toAcquiringEntity = (row: EntityRow): AcquiringEntity => ({
  id: row.id,
  name: row.name,
  entityClassification: row.entityClassification,
  entityType: row.entityType,
  crmTractCount: row.crmTractCount,
  titleAssetCount: row.titleAssetCount,
  crmAccountCount: row.crmAccountCount,
});

/**
 * Inner body. Owns pending-then-save state; mounts only while the Drawer
 * is open so pending state resets on reopen. Delegates the actual search +
 * table rendering to the shared {@link EntitySearchTable} — this component
 * only layers the Save/Cancel commit semantics on top.
 *
 * @param {EntityPickerPanelBodyProps} props - Selection + commit/dismiss callbacks
 * @return {JSX.Element} The panel body
 */
const EntityPickerPanelBody: FC<EntityPickerPanelBodyProps> = ({
  selectedEntity,
  onSelect,
  onClose,
}) => {
  const [pendingEntity, setPendingEntity] = useState<AcquiringEntity | null>(
    selectedEntity
  );

  const handleSave = (): void => {
    if (pendingEntity === null) return;
    onSelect(pendingEntity);
    onClose();
  };

  return (
    <div className={styles.layout}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>Acquiring Entity</h3>
          <p className={styles.subtitle}>
            Select the acquiring entity for this package.
          </p>
        </div>
        <Button
          kind="ghost"
          hasIconOnly
          size="lg"
          renderIcon={(props) => <Close size={20} {...props} />}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          onClick={onClose}
        />
      </div>
      <div className={styles.body}>
        <PackageEntityTable
          searchAtom={packageAcquirerEntitySearchAtom}
          selectedId={pendingEntity?.id ?? null}
          onSelect={(row) => setPendingEntity(toAcquiringEntity(row))}
        />
      </div>
      <div className={styles.footer}>
        <Button
          kind="secondary"
          size="lg"
          className={styles.footerButton}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          className={styles.footerButton}
          disabled={pendingEntity === null}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

/**
 * Right-side slide-over that lists entities and commits one as the
 * Acquiring Entity via a pending-then-save flow. Cancel, ESC, or overlay
 * click discards the pending choice. Backed by the in-house
 * {@link Drawer} (react-modern-drawer) — body mounts only while open so
 * pending state resets naturally on reopen.
 *
 * @param {EntityPickerPanelProps} props - Open flag + selection/commit/dismiss
 * @return {JSX.Element} The slide-over panel
 */
export const EntityPickerPanel: FC<EntityPickerPanelProps> = ({
  open,
  selectedEntity,
  onSelect,
  onClose,
}) => (
  <Drawer
    open={open}
    onClose={onClose}
    direction="right"
    size={900}
    lockBackgroundScroll
  >
    {open && (
      <EntityPickerPanelBody
        selectedEntity={selectedEntity}
        onSelect={onSelect}
        onClose={onClose}
      />
    )}
  </Drawer>
);
